import { HELP_LAYOUT_TYPE } from 'yeself-common/enum/LayoutEnum';
import {
    LOCATION_HREF,
    BORDER_TYPE,
    SHADOW_TYPE,
    SHADOW_ANIMATION_TYPE,
    BORDER_ANIMATION_TYPE,
    STEP_ANIMATION_TYPE,
    ELEMENT_PATHS,
    PREVENT_INTERACTION_TYPE,
} from '../ModelConstant';
import { ORIENTATION } from '../model/PositionEnum';
import { NO_PATH, NO_PATTERN, ELEMENT_PATH_SEARCH_STRATEGY } from '../enum/ModelEnum';
import { LAYER_NODE_PERSIST } from '../typings/guide/LayerEnum';

const DEFAULT_ELEMENT_ZOOM_STRENGTH = 1.2;

/**
 * @type {ElementPadding}
 */
export const ELEMENT_PADDING_DEFAULT = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
};

/**
 * @type {NodeBorder}
 */
const NODE_BORDER_DEFAULT = {
    type: BORDER_TYPE.NO,
    animation: BORDER_ANIMATION_TYPE.NO,
};

/**
 * @type {NodeShadow}
 */
const NODE_SHADOW_DEFAULT = {
    type: SHADOW_TYPE.TRANSPARENT,
    animation: SHADOW_ANIMATION_TYPE.NO,
};

const NODE_CLOSE_BUTTON_DEFAULT = {
    visible: false,
};

const NODE_CLOSE_BUTTON_PLAYLIST_DEFAULT = {
    visible: true,
};

const NODE_CLOSE_CROSS_BUTTON_DEFAULT = {
    visible: true,
};

const NODE_ARROW_DEFAULT = {
    visible: true,
};

const NODE_ARROW_PLAYLIST_DEFAULT = {
    visible: false,
};

const NODE_STEP_COUNTER_DEFAULT = {
    visible: true,
};

const NODE_STEP_COUNTER_PLAYLIST_DEFAULT = {
    visible: false,
};

const NODE_STEP_COUNTER_TOTAL_DEFAULT = {
    visible: false,
};

const NODE_STEP_ENCAPSULATION_DEFAULT = {
    enabled: false,
};

/**
 * @type {NodeLayoutComponent}
 */
const NODE_LAYOUT_COMPONENT_DEFAULT = {
    arrow: NODE_ARROW_DEFAULT,
    closeActionButton: NODE_CLOSE_BUTTON_DEFAULT,
    closeCrossButton: NODE_CLOSE_CROSS_BUTTON_DEFAULT,
    stepCounter: NODE_STEP_COUNTER_DEFAULT,
    stepCounterTotal: NODE_STEP_COUNTER_TOTAL_DEFAULT,
    stepEncapsulation: NODE_STEP_ENCAPSULATION_DEFAULT,
    shadow: NODE_SHADOW_DEFAULT,
    border: NODE_BORDER_DEFAULT,
};

const NODE_LAYOUT_LIMITS_DEFAULT = {
    maxWidth: {
        enabled: false,
        value: ``,
    },
    maxHeight: {
        enabled: false,
        value: '',
    },
};

/**
 * @type {NodeLayout}
 */
const NODE_LAYOUT_DEFAULT = {
    preventInteraction: PREVENT_INTERACTION_TYPE.OFF,
    preventScroll: false,
    type: HELP_LAYOUT_TYPE.BUBBLE,
    component: NODE_LAYOUT_COMPONENT_DEFAULT,
    limits: NODE_LAYOUT_LIMITS_DEFAULT,
};

const NODE_PLAYLIST_LAYOUT_DEFAULT = {
    ...NODE_LAYOUT_DEFAULT,
    type: HELP_LAYOUT_TYPE.PANEL,
    component: {
        ...NODE_LAYOUT_DEFAULT.component,
        arrow: NODE_ARROW_PLAYLIST_DEFAULT,
        closeActionButton: NODE_CLOSE_BUTTON_PLAYLIST_DEFAULT,
        stepCounter: NODE_STEP_COUNTER_PLAYLIST_DEFAULT,
    },
};

/**
 * @type {CustomRegion}
 */
export const CUSTOM_REGION_DEFAULT = {
    elementPadding: ELEMENT_PADDING_DEFAULT,
    cropToContent: false,
};

/**
 * @type {AnimationTransition}
 */
export const NODE_STEP_ANIMATION_DEFAULT = {
    enter: STEP_ANIMATION_TYPE.NO,
    leave: STEP_ANIMATION_TYPE.NO,
};

/**
 * @type {AnimationTransition}
 */
export const NODE_ELEMENT_ANIMATION_DEFAULT = {
    enter: STEP_ANIMATION_TYPE.NO,
    leave: STEP_ANIMATION_TYPE.NO,
    zoomStrength: DEFAULT_ELEMENT_ZOOM_STRENGTH,
};

export const NODE_ANIMATION_DEFAULT = {
    step: NODE_STEP_ANIMATION_DEFAULT,
    element: NODE_ELEMENT_ANIMATION_DEFAULT,
};

/**
 * @type {NodeContainer}
 */
export const NODE_CONTAINER_DEFAULT = {
    location: NO_PATH,
};

export const EVENT_EFFECT_DEFAULT = {
    name: 'yeself-effect-none',
};

/**
 * @type {Array<TriggerEvent>}
 */
export const TRIGGER_EVENT_DEFAULT = [];

/**
 * @type {KickTrigger}
 * @deprecated
 */
export const KICK_EDGE_DEFAULT = {
    event: TRIGGER_EVENT_DEFAULT,
    definition: [],
};

/**
 * @type {TourStepNodeCompletionEdge}
}
 */
export const COMPLETION_EDGE_DEFAULT = {
    next: true,
    previous: true,
    immediate: false,
    event: TRIGGER_EVENT_DEFAULT,
    definition: [],
};

export const COMPLETION_EDGE_PLAYLIST_DEFAULT = {
    ...COMPLETION_EDGE_DEFAULT,
    previous: false,
};

/**
 * @type {TourStepNodeEdges}
 */
export const NODE_EDGES_DEFAULT = {
    kick: KICK_EDGE_DEFAULT,
    completion: COMPLETION_EDGE_DEFAULT,
    eventCommands: {},
};

export const NODE_PLAYLIST_EDGES_DEFAULT = {
    ...NODE_EDGES_DEFAULT,
    completion: COMPLETION_EDGE_PLAYLIST_DEFAULT,
};

/**
 * @type {NodeLocation}
 */
export const LOCATION_DEFAULT = {
    paths: [
        {
            id: ELEMENT_PATHS.XPATH,
            path: {
                en: NO_PATH,
            },
            enabled: true,
            searchStrategy: ELEMENT_PATH_SEARCH_STRATEGY.EXACT_ROOT_MATCH,
        },
        {
            id: ELEMENT_PATHS.RELATIVE,
            path: NO_PATH,
            enabled: true,
            searchStrategy: ELEMENT_PATH_SEARCH_STRATEGY.EXACT_ROOT_MATCH,
        },
        {
            id: ELEMENT_PATHS.XPATH_LIMITED,
            path: NO_PATH,
            enabled: true,
            searchStrategy: ELEMENT_PATH_SEARCH_STRATEGY.EXACT_ROOT_MATCH,
        },
        {
            id: ELEMENT_PATHS.RELATIVE_LIMITED,
            path: NO_PATH,
            enabled: true,
            searchStrategy: ELEMENT_PATH_SEARCH_STRATEGY.EXACT_ROOT_MATCH,
        },
        {
            id: ELEMENT_PATHS.CLASS_ONLY,
            path: NO_PATH,
            enabled: true,
            searchStrategy: ELEMENT_PATH_SEARCH_STRATEGY.EXACT_ROOT_MATCH,
        },
        {
            id: ELEMENT_PATHS.EXPRESSED,
            path: NO_PATH,
            enabled: true,
            searchStrategy: ELEMENT_PATH_SEARCH_STRATEGY.EXACT_ROOT_MATCH,
        },
        {
            id: ELEMENT_PATHS.TAG_ONLY,
            path: NO_PATH,
            enabled: true,
            searchStrategy: ELEMENT_PATH_SEARCH_STRATEGY.EXACT_ROOT_MATCH,
        },
        {
            id: ELEMENT_PATHS.JS_PATH,
            path: NO_PATH,
            enabled: false,
            searchStrategy: ELEMENT_PATH_SEARCH_STRATEGY.JS_PATH_QUERY,
        },
        {
            id: ELEMENT_PATHS.JS_PATH_NON_RELATIVE,
            path: NO_PATH,
            enabled: false,
            searchStrategy: ELEMENT_PATH_SEARCH_STRATEGY.JS_PATH_QUERY,
        },
    ],
    id: 'u-u-i-d',
    representativeFeatures: [],
};

/**
 * @type {Href}
 */
export const HREF_DEFAULT = {
    pattern: LOCATION_HREF.NO_PATTERN,
    url: LOCATION_HREF.NO_URL,
};

/**
 * @type {NodeNavigation}
 */
const NODE_NAVIGATION_DEFAULT = {
    href: HREF_DEFAULT,
    navigate: false,
    ignoreQueryParameters: true,
    ignoreNavigation: false,
};

/**
 * @type {NodePositionZIndex}
 */
export const NODE_POSITION_Z_INDEX_DEFAULT = {
    source: 'THEME',
    bubbleZIndex: 0,
    shadowZIndex: 0,
};

export const NODE_CUSTOM_OFFSET_DEFAULT = {
    enabled: false,
    value: 0,
};

/**
 * @type {NodePosition}
 */
const NODE_POSITION_DEFAULT = {
    orientation: ORIENTATION.AUTO,
    customRegion: CUSTOM_REGION_DEFAULT,
    scrollToStep: true,
    customOffset: NODE_CUSTOM_OFFSET_DEFAULT,
    centerScroll: false,
    notCovered: false,
    type: 'relative',
    container: NODE_CONTAINER_DEFAULT,
    updateOnScroll: false,
    zIndex: NODE_POSITION_Z_INDEX_DEFAULT,
};

/**
 * @type {SoundContent}
 */
const NODE_SOUND_DEFAULT = {
    src: {},
    enabled: false,
    autoplay: true,
};

/**
 * @type {HelpContentOption}
 */
const HELP_OPTION_DEFAULT = {
    done: {},
    next: {},
    previous: {},
    step: {},
    close: {},
};

/**
 * @type {HelpContent}
 */
const HELP_CONTENT_DEFAULT = {
    text: {},
    previewText: {},
    sound: NODE_SOUND_DEFAULT,
    option: HELP_OPTION_DEFAULT,
};

/**
 * @type {ProgressTrackerContent}
 */
const PROGRESS_TRACKER_CONTENT_DEFAULT = {
    title: {},
    text: {},
};

/**
 * @type {NodeContent}
 */
const NODE_CONTENT_DEFAULT = {
    help: HELP_CONTENT_DEFAULT,
    progressTracker: PROGRESS_TRACKER_CONTENT_DEFAULT,
};

export const NODE_SLAVE_DATA_FRAME_DEFAULT = {
    name: '',
    pattern: NO_PATTERN,
};

export const NODE_SLAVE_DATA_DEFAULT = {
    enabled: false,
    frame: NODE_SLAVE_DATA_FRAME_DEFAULT,
};

const NODE_ADDON_DEFAULT = {
    name: '',
};

/**
 * @type {NodeMetadata}
 */
export const NODE_METADATA_DEFAULT = {
    content: NODE_CONTENT_DEFAULT,
    animation: NODE_ANIMATION_DEFAULT,
    edges: NODE_EDGES_DEFAULT,
    layout: NODE_LAYOUT_DEFAULT,
    location: LOCATION_DEFAULT,
    navigation: NODE_NAVIGATION_DEFAULT,
    systemPages: [],
    position: NODE_POSITION_DEFAULT,
    persistStep: LAYER_NODE_PERSIST.ANY,
    slaveData: NODE_SLAVE_DATA_DEFAULT,
    addon: NODE_ADDON_DEFAULT,
};

/**
 * @type {TourNode}
 */
export const NODE_DEFAULT = {
    id: '',
    metadata: NODE_METADATA_DEFAULT,
};

/**
 * @type {HelpContentOption}
 */
const NODE_HELP_OPTION_DEFAULT = {
    next: {},
    previous: {},
    close: {},
    step: {},
    done: {},
};

/**
 * @type {HelpContent}
 */
const NODE_HELP_CONTENT_DEFAULT = {
    option: NODE_HELP_OPTION_DEFAULT,
    text: {},
    previewText: {},
    sound: NODE_SOUND_DEFAULT,
};

/**
 * @type {NodeCommand}
 */
const NODE_SEND_COMMAND_DEFAULT = {
    send: '',
};

export {
    NODE_LAYOUT_DEFAULT,
    NODE_PLAYLIST_LAYOUT_DEFAULT,
    NODE_ARROW_DEFAULT,
    NODE_CLOSE_BUTTON_DEFAULT,
    NODE_CLOSE_CROSS_BUTTON_DEFAULT,
    NODE_STEP_COUNTER_DEFAULT,
    NODE_STEP_COUNTER_TOTAL_DEFAULT,
    NODE_LAYOUT_COMPONENT_DEFAULT,
    NODE_BORDER_DEFAULT,
    NODE_SHADOW_DEFAULT,
    NODE_SOUND_DEFAULT,
    NODE_HELP_CONTENT_DEFAULT,
    NODE_HELP_OPTION_DEFAULT,
    NODE_NAVIGATION_DEFAULT,
    NODE_POSITION_DEFAULT,
    NODE_CONTENT_DEFAULT,
    HELP_CONTENT_DEFAULT,
    PROGRESS_TRACKER_CONTENT_DEFAULT,
    HELP_OPTION_DEFAULT,
    DEFAULT_ELEMENT_ZOOM_STRENGTH,
    NODE_SEND_COMMAND_DEFAULT,
    NODE_LAYOUT_LIMITS_DEFAULT,
    NODE_ADDON_DEFAULT,
};
